import { Link } from "react-router-dom";
import type { Actress } from "@/lib/types.ts";

interface ActressCardProps {
  actress: Actress;
}

function NoImage({ name }: { name: string }) {
  return (
    <div className="w-full h-full bg-secondary flex items-center justify-center">
      <span className="text-3xl font-bold text-muted-foreground/40">{name.charAt(0)}</span>
    </div>
  );
}

export function ActressCard({ actress }: ActressCardProps) {
  const hasBwh = actress.bust > 0 || actress.waist > 0 || actress.hip > 0;

  return (
    <Link
      to={`/actress/${actress.id}`}
      className="group block rounded-xl overflow-hidden bg-card border border-border shadow-sm hover:shadow-md transition-shadow no-underline"
      viewTransition
    >
      <div className="w-full aspect-square overflow-hidden">
        {actress.imageUrl ? (
          <img
            src={actress.imageUrl}
            alt={actress.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            style={{ viewTransitionName: `actress-img-${actress.id}` }}
          />
        ) : (
          <NoImage name={actress.name} />
        )}
      </div>
      <div className="p-4 flex flex-col gap-2.5">
        <div className="flex items-center justify-between">
          <h3
            className="font-[var(--font-secondary)] text-sm font-semibold text-foreground"
            style={{ viewTransitionName: `actress-name-${actress.id}` }}
          >
            {actress.name}
          </h3>
          <div className="flex flex-col items-end gap-0.5">
            {actress.debutAge != null && actress.latestActiveAge != null ? (
              <span className="font-[var(--font-secondary)] text-xs text-muted-foreground">
                出演{actress.debutAge === actress.latestActiveAge
                  ? `${actress.debutAge}歳`
                  : `${actress.debutAge}〜${actress.latestActiveAge}歳`}
              </span>
            ) : actress.age > 0 ? (
              <span className="font-[var(--font-secondary)] text-xs text-muted-foreground">
                {actress.age}歳
              </span>
            ) : null}
          </div>
        </div>
        {hasBwh && (
          <p className="font-[var(--font-primary)] text-xs text-muted-foreground">
            {actress.bust > 0 ? `B${actress.bust}` : "-"} / {actress.waist > 0 ? `W${actress.waist}` : "-"} / {actress.hip > 0 ? `H${actress.hip}` : "-"}
            {actress.cupSize && <>&ensp;・&ensp;{actress.cupSize}カップ</>}
          </p>
        )}
        {actress.tags.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {actress.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground font-[var(--font-secondary)] text-[11px]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
